import React, {
  forwardRef,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  faChevronDown,
  faChevronRight,
  faCopy,
  faInfoCircle,
  IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import { faWarning } from "@fortawesome/pro-solid-svg-icons";
import { twJoin } from "tailwind-merge";
import { useHover } from "usehooks-ts";

import { Provider } from "src/api/connectApi/types";
import { DatasetColumnGroup, DesiredType } from "src/api/types";
import { CustomPopover } from "src/base-components/Popover";
import { ProviderIcon } from "src/connections/config/Icon";
import { NODE_TYPE } from "src/constants/NodeTypes";
import { ColumnMenu } from "src/datasets/DatasetTable/ColumnMenu";
import {
  useIsColumnHovered,
  useIsColumnHoveredLike,
  useIsEditingCell,
  useIsSelectedCell,
} from "src/datasets/DatasetTable/stores";
import { DatasetContext, JSONValue } from "src/datasets/DatasetTable/types";
import {
  AvailableOutcomeColumn,
  CellId,
  getNonProviderIntegrationNodeIcon,
  isNonProviderIntegrationNode,
  jsonToPon,
  parseCellId,
} from "src/datasets/DatasetTable/utils";
import { DatasetIntegrationNode } from "src/datasets/utils";
import { ObjectDetailPane } from "src/dataTable/ObjectDetailPane";
import { Icon } from "src/design-system/Icon";
import { Tooltip, TooltipProps } from "src/design-system/Tooltip";
import { copyTextToClipboard } from "src/utils/clipboard";
import { TYPE_ICONS } from "src/utils/constants";

export const getIcon = (type: DesiredType) => TYPE_ICONS[type];

export type MockColumn = {
  name: string;
  provider:
    | Provider
    | string
    | NODE_TYPE.FLOW_NODE
    | NODE_TYPE.LOOP_NODE
    | NODE_TYPE.READ_ENTITY_NODE
    | NODE_TYPE.AI_NODE_V2;
  mediaKey: string | null;
  subMocks: Pick<MockColumn, "name" | "provider" | "mediaKey">[] | null;
};

export const ReadonlyCell: React.FC<{
  children: string | undefined;
  withPopup: boolean;
}> = ({ children, withPopup }) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const wrapWithPopupIfNeeded = (mainElement: JSX.Element) => {
    if (withPopup && children) {
      return (
        <CustomPopover
          button={mainElement}
          isOpen={tooltipOpen}
          placement="top"
          onMouseEnter={() => setTooltipOpen(true)}
          onMouseLeave={() => setTooltipOpen(false)}
        >
          <div className="decideScrollbar z-50 max-h-150 min-w-[220px] max-w-128 overflow-auto px-3 pt-3 pb-2">
            <ObjectDetailPane
              innerDimensionClass="max-h-64 max-w-164"
              jsonObject={JSON.parse(children)}
              speakPython
            />
          </div>
        </CustomPopover>
      );
    } else {
      return mainElement;
    }
  };

  return wrapWithPopupIfNeeded(
    <div className="max-w-40 truncate p-1.5 font-inter-normal-12px text-gray-800">
      {children ? jsonToPon(children) : ""}
    </div>,
  );
};

type HeaderProps = {
  children: string;
  type?: DesiredType;
  cellId: CellId;
  mock: {
    mockFields: MockColumn[];
    integrationNodesExist: boolean;
  };
  onDelete: () => void;
  onRename: (newName: string) => void;
  onColumnAdd: (name: string, group: DatasetColumnGroup) => void;
  onAddSubMocks?: (
    subMockNames: string[],
    mockColumnName: string,
    desiredType: Extract<DesiredType, "object" | "any">,
  ) => void;
  onAddAdditionalColumn: () => void;
  output: { outputFields: string[]; schemaIsEmpty: boolean };
  input: { inputFields: string[]; schemaIsEmpty: boolean };
  outcome: {
    outcomeFields: AvailableOutcomeColumn[];
    thereAreNoOutcomes: boolean;
  };
  readonly: boolean;
  warning?: Pick<TooltipProps, "title" | "body" | "headerAction">;
  disabled?: boolean;
  typeChange?: {
    selectedType: DesiredType;
    compatibleType?: DesiredType;
    onChangeType: (selectedType: DesiredType) => void;
  };
  fillMockColumn?: {
    integrationNode: DatasetIntegrationNode;
    onFill: (value: JSONValue) => void;
  };
  context: DatasetContext;
  customIcon?: IconDefinition;
};

export const Header: React.FC<HeaderProps> = ({
  children,
  type,
  cellId,
  onDelete,
  onRename,
  readonly,
  onColumnAdd,
  onAddSubMocks,
  warning,
  typeChange,
  mock,
  fillMockColumn,
  input,
  output,
  outcome,
  disabled,
  onAddAdditionalColumn,
  context,
  customIcon,
}) => {
  const editorRef = useRef<HTMLInputElement>(null);
  const cellRef = useRef<HTMLDivElement>(null);
  const [localValue, setLocalValue] = useState(children);
  const [_, columnId] = parseCellId(cellId);
  const selected = useIsSelectedCell(cellId);
  const editing = useIsEditingCell(cellId);
  const isMenuVisible = useIsColumnHovered(columnId) || selected || editing;

  useEffect(() => {
    if (editing && editorRef.current) {
      editorRef.current.focus();
    }
  }, [editing]);

  useEffect(() => {
    if (selected && !editing && cellRef.current) {
      cellRef.current.focus();
      cellRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "nearest",
        block: "nearest",
      });
    }
  }, [selected, editing]);

  useEffect(() => {
    setLocalValue(children);
  }, [children]);

  return (
    <HeaderCell
      ref={cellRef}
      className={twJoin(
        "group gap-x-0.5",
        getStateColor({ disabled, warning: !!warning }),
        selected && "outline outline-1 outline-indigo-500",
      )}
      dataLoc={`dataset-header-${localValue}`}
    >
      {(() => {
        if (fillMockColumn) {
          return isNonProviderIntegrationNode(
            fillMockColumn.integrationNode,
          ) ? (
            <Icon
              icon={getNonProviderIntegrationNodeIcon(
                fillMockColumn.integrationNode.provider,
              )}
              size="2xs"
            />
          ) : (
            <ProviderIcon
              mediaKey={fillMockColumn.integrationNode.mediaKey}
              provider={fillMockColumn.integrationNode.provider as Provider}
              size="sm"
            />
          );
        }

        return <Icon icon={customIcon ?? getIcon(type ?? "any")} size="2xs" />;
      })()}

      <Tooltip
        headerAction={{
          icon: faCopy,
          onMouseDownCapture: () => {
            copyTextToClipboard(children);
          },
        }}
        placement="top"
        title={children}
        asChild
      >
        <input
          ref={editorRef}
          className={twJoin(
            "w-full bg-transparent outline-none [&:not(:focus)]:text-ellipsis",
            (readonly || disabled) && "cursor-default",
            disabled && "text-gray-500",
          )}
          data-loc="dataset-table-header-cell"
          readOnly={readonly || disabled}
          spellCheck={false}
          value={localValue}
          onBlur={() => {
            onRename(localValue);
          }}
          onChange={(e) => setLocalValue(e.target.value)}
        />
      </Tooltip>
      <div className="ml-auto flex items-center">
        {!readonly && (
          <ColumnMenu
            key={cellId}
            cellId={cellId}
            columnDisabled={disabled}
            context={context}
            fillMockColumn={fillMockColumn}
            input={input}
            mock={mock}
            outcome={outcome}
            output={output}
            typeChange={typeChange}
            visible={isMenuVisible}
            onAdd={onColumnAdd}
            onAddAdditionalColumn={onAddAdditionalColumn}
            onAddSubMocks={onAddSubMocks}
            onDelete={onDelete}
          />
        )}
        {warning && (
          <Tooltip placement="right" {...warning}>
            <Icon variant="warning" icon={faWarning} size="2xs" />
          </Tooltip>
        )}
      </div>
    </HeaderCell>
  );
};

export const NoMenuHeader: React.FC<{
  children: string;
  type: DesiredType;
  icon?: ReactNode;
  warning?: Pick<TooltipProps, "title" | "body" | "headerAction">;
  info?: Pick<TooltipProps, "title" | "body" | "headerAction">;
}> = ({ children, type, icon, warning, info }) => (
  <HeaderCell
    className={twJoin("group gap-x-0.5", warning ? "bg-orange-50" : "bg-white")}
    dataLoc={`dataset-header-${children}`}
  >
    {icon || <Icon icon={getIcon(type)} size="2xs" />}

    <Tooltip
      headerAction={{
        icon: faCopy,
        onMouseDownCapture: () => {
          copyTextToClipboard(children);
        },
      }}
      placement="top"
      title={children}
    >
      {children}
    </Tooltip>
    <div className="ml-auto flex items-center">
      {warning && (
        <Tooltip placement="right" {...warning}>
          <Icon variant="warning" icon={faWarning} size="2xs" />
        </Tooltip>
      )}
      {info && (
        <Tooltip placement="right-start" {...info}>
          <Icon icon={faInfoCircle} size="2xs" />
        </Tooltip>
      )}
    </div>
  </HeaderCell>
);

type SubGroupHeaderProps = {
  children: string;
  cellId: CellId;
  mock: {
    mockFields: MockColumn[];
    integrationNodesExist: boolean;
  };
  onDelete: () => void;
  onColumnAdd: (name: string, group: DatasetColumnGroup) => void;
  onDeleteSubMocks: () => void;
  onAddAdditionalColumn: () => void;
  onRename?: (newName: string) => void;
  output: { outputFields: string[]; schemaIsEmpty: boolean };
  input: { inputFields: string[]; schemaIsEmpty: boolean };
  outcome: {
    outcomeFields: AvailableOutcomeColumn[];
    thereAreNoOutcomes: boolean;
  };
  readonly: boolean;
  warning?: Pick<TooltipProps, "title" | "body" | "headerAction">;
  disabled?: boolean;
  integrationNode: DatasetIntegrationNode | null;
  desiredType?: DesiredType;
  customIcon?: ReactNode;
  typeChange?: {
    selectedType: DesiredType;
    compatibleType?: DesiredType;
    onChangeType: (selectedType: DesiredType) => void;
  };
  onToggleVisibility?: () => void;
  isCollapsed?: boolean;
};

export const SubGroupHeader: React.FC<SubGroupHeaderProps> = ({
  children,
  cellId,
  integrationNode,
  onDelete,
  onDeleteSubMocks,
  readonly,
  onColumnAdd,
  onRename,
  warning,
  mock,
  input,
  output,
  outcome,
  disabled,
  desiredType,
  onAddAdditionalColumn,
  customIcon,
  typeChange,
  onToggleVisibility,
  isCollapsed,
}) => {
  const [_, columnId] = parseCellId(cellId);
  const editorRef = useRef<HTMLInputElement>(null);
  const cellRef = useRef<HTMLDivElement>(null);
  const isHovered = useHover(cellRef);
  const isColumnHovered = useIsColumnHoveredLike(columnId);
  const selected = useIsSelectedCell(cellId);
  const editing = useIsEditingCell(cellId);

  const isMenuVisible = Boolean(
    isHovered || isColumnHovered || selected || editing,
  );

  const [localValue, setLocalValue] = useState(children);

  useEffect(() => {
    if (editing && editorRef.current) {
      editorRef.current.focus();
    }
  }, [editing]);

  useEffect(() => {
    if (selected && !editing && cellRef.current) {
      cellRef.current.focus();
      cellRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "nearest",
        block: "nearest",
      });
    }
  }, [selected, editing]);

  useEffect(() => {
    setLocalValue(children);
  }, [children]);

  return (
    <HeaderCell
      ref={cellRef}
      className={twJoin(
        "group gap-x-0.5 border-gray-100 border-b",
        getStateColor({ disabled, warning: !!warning }),
        selected && "outline outline-1 outline-indigo-500",
      )}
      dataLoc={`dataset-header-${localValue}`}
    >
      {onToggleVisibility && (
        <Tooltip
          title={isCollapsed ? "Expand properties" : "Collapse properties"}
          asChild
        >
          <button
            className="text-gray-500 hover:text-gray-700"
            onClick={onToggleVisibility}
          >
            <Icon
              icon={isCollapsed ? faChevronDown : faChevronRight}
              size="xs"
            />
          </button>
        </Tooltip>
      )}
      {desiredType || integrationNode ? (
        <Icon
          icon={
            integrationNode
              ? getNonProviderIntegrationNodeIcon(integrationNode.provider)
              : getIcon(desiredType ?? "any")
          }
          size="2xs"
        />
      ) : (
        customIcon
      )}
      <Tooltip
        headerAction={{
          icon: faCopy,
          onMouseDownCapture: () => {
            copyTextToClipboard(children);
          },
        }}
        placement="top"
        title={children}
        asChild
      >
        <input
          ref={editorRef}
          className={twJoin(
            "w-full bg-transparent outline-none [&:not(:focus)]:text-ellipsis",
            (readonly || disabled || !onRename) && "cursor-default",
            disabled && "text-gray-500",
          )}
          data-loc="dataset-table-header-cell"
          readOnly={readonly || disabled || !onRename}
          spellCheck={false}
          value={localValue}
          onBlur={() => {
            onRename?.(localValue);
          }}
          onChange={(e) => setLocalValue(e.target.value)}
        />
      </Tooltip>
      <div className="ml-auto flex items-center">
        {!readonly && (
          <ColumnMenu
            cellId={cellId}
            columnDisabled={disabled}
            context="authoring"
            fillMockColumn={integrationNode ? { integrationNode } : undefined}
            input={input}
            mock={mock}
            outcome={outcome}
            output={output}
            typeChange={typeChange}
            visible={isMenuVisible}
            onAdd={onColumnAdd}
            onAddAdditionalColumn={onAddAdditionalColumn}
            onDelete={onDelete}
            onDeleteSubMocks={onDeleteSubMocks}
          />
        )}
        {warning && (
          <Tooltip placement="right" {...warning}>
            <Icon icon={faWarning} variant="warning" size="2xs" />
          </Tooltip>
        )}
      </div>
    </HeaderCell>
  );
};

type GroupHeaderProps = {
  children: ReactNode;
  variant: "gray" | "indigo" | "dark-gray" | "green";
};

export const GroupHeader: React.FC<GroupHeaderProps> = ({
  children,
  variant,
}) => (
  <HeaderCell
    className={twJoin(
      variant === "gray" && "bg-gray-50",
      variant === "indigo" && "bg-indigo-50",
      variant === "dark-gray" && "bg-gray-200",
      variant === "green" && "bg-green-50",
    )}
  >
    {children}
  </HeaderCell>
);

const HeaderCell = forwardRef<
  HTMLDivElement,
  { children: ReactNode; className?: string; dataLoc?: string }
>(({ children, className, dataLoc }, ref) => (
  <div
    ref={ref}
    className={twJoin(
      "flex w-full items-center p-1.5 font-inter-medium-12px text-gray-800",
      className,
    )}
    data-loc={dataLoc}
    tabIndex={-1}
  >
    {children}
  </div>
));

const getStateColor = ({
  disabled,
  warning,
}: {
  disabled?: boolean;
  warning?: boolean;
}) => {
  if (disabled) return "bg-gray-50 text-gray-500";

  if (warning) return "bg-orange-50";

  return "bg-white";
};
